import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents } from '@pirouette/shared';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@/lib/supabase';
import { useFamilyTickets } from '@/data/stripe';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { ticketQrPayload } from '@/lib/ticketQr';

export const Route = createFileRoute('/parent/billing/tickets')({
  component: ParentTickets,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const u = await supabase.auth.getUser();
      return (u.data.user?.app_metadata.family_id as string | undefined) ?? null;
    },
  });
}

interface SeatDetail {
  id: string;
  row_label: string;
  seat_number: number;
  section: { name: string };
}

interface ShowDetail {
  id: string;
  name: string;
  performance_at: string;
  production: { name: string };
}

function useTicketDecorations(ticketIds: string[]) {
  return useQuery({
    enabled: ticketIds.length > 0,
    queryKey: ['ticket-decorations', ticketIds.sort().join('|')],
    queryFn: async () => {
      // Pull seat + show + section in two queries (avoids N+1 client-side).
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select(
          'id, seat_id, show_id, seats(row_label, seat_number, seat_map_sections(name)), shows(name, performance_at, productions:production_id(name))',
        )
        .in('id', ticketIds);
      if (error) throw error;
      const seatMap = new Map<string, SeatDetail>();
      const showMap = new Map<string, ShowDetail>();
      for (const t of (tickets ?? []) as {
        id: string;
        seat_id: string;
        show_id: string;
        seats: {
          row_label: string;
          seat_number: number;
          seat_map_sections: { name: string };
        };
        shows: {
          name: string;
          performance_at: string;
          productions: { name: string };
        };
      }[]) {
        seatMap.set(t.id, {
          id: t.seat_id,
          row_label: t.seats.row_label,
          seat_number: t.seats.seat_number,
          section: { name: t.seats.seat_map_sections.name },
        });
        showMap.set(t.id, {
          id: t.show_id,
          name: t.shows.name,
          performance_at: t.shows.performance_at,
          production: { name: t.shows.productions.name },
        });
      }
      return { seats: seatMap, shows: showMap };
    },
  });
}

function ParentTickets() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const tickets = useFamilyTickets(familyId);
  const decorations = useTicketDecorations((tickets.data ?? []).map((t) => t.id));

  // Group tickets by show, sorted by show date ascending then by seat.
  const groups = new Map<
    string,
    {
      show: ShowDetail;
      tickets: {
        id: string;
        seat: SeatDetail;
        price_cents: number;
        status: string;
        qr_token: string | null;
        checked_in_at: string | null;
      }[];
    }
  >();
  for (const t of tickets.data ?? []) {
    const seat = decorations.data?.seats.get(t.id);
    const show = decorations.data?.shows.get(t.id);
    if (!seat || !show) continue;
    if (!groups.has(t.show_id)) groups.set(t.show_id, { show, tickets: [] });
    groups.get(t.show_id)!.tickets.push({
      id: t.id,
      seat,
      price_cents: t.price_cents,
      status: t.status,
      qr_token: t.qr_token,
      checked_in_at: t.checked_in_at,
    });
  }
  const grouped = [...groups.values()].sort(
    (a, b) => new Date(a.show.performance_at).getTime() - new Date(b.show.performance_at).getTime(),
  );

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Your tickets</h1>
      </header>

      {(tickets.isLoading || decorations.isLoading) && <p aria-live="polite">Loading tickets…</p>}

      {!tickets.isLoading && grouped.length === 0 && (
        <Empty
          title="No tickets yet"
          description="When you purchase seats, they'll appear here grouped by show."
        />
      )}

      <ul className="space-y-6">
        {grouped.map(({ show, tickets: ts }) => (
          <li key={show.id} className="surface-elevated p-5">
            <header>
              <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
                {show.production.name}
              </p>
              <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">{show.name}</h2>
              <p className="mt-1 text-[var(--color-ink-muted)]">
                {new Date(show.performance_at).toLocaleString()}
              </p>
            </header>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {ts.map((t) => (
                <li key={t.id} className="surface grid gap-4 p-3 sm:grid-cols-[1fr_auto]">
                  <div className="space-y-2">
                    <div>
                      <p className="font-medium">
                        {t.seat.section.name} · Row {t.seat.row_label} · Seat {t.seat.seat_number}
                      </p>
                      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
                        {formatCents(t.price_cents)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        tone={
                          t.status === 'sold'
                            ? 'success'
                            : t.status === 'refunded'
                              ? 'warn'
                              : 'neutral'
                        }
                      >
                        {t.status}
                      </Badge>
                      {t.checked_in_at ? (
                        <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                          Checked in {new Date(t.checked_in_at).toLocaleString()}
                        </span>
                      ) : t.status === 'sold' ? (
                        <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                          Ready for admission
                        </span>
                      ) : (
                        <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                          Not valid for admission
                        </span>
                      )}
                    </div>
                  </div>
                  {t.status === 'sold' && t.qr_token ? (
                    <div className="max-w-44 justify-self-start sm:justify-self-end">
                      <div className="rounded border border-[var(--color-border)] bg-white p-2">
                        <QRCodeSVG
                          value={ticketQrPayload(t.qr_token)}
                          size={112}
                          level="M"
                          marginSize={4}
                          title={`Admission QR code for ${t.seat.section.name} row ${t.seat.row_label} seat ${t.seat.seat_number}`}
                          role="img"
                          aria-label={`Admission QR code for ${t.seat.section.name} row ${t.seat.row_label} seat ${t.seat.seat_number}`}
                        />
                      </div>
                      <p className="mt-2 leading-snug text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                        Treat this QR code like a ticket. Screenshots or forwarded images can be
                        scanned. Contact the studio if you need a replacement QR code.
                      </p>
                    </div>
                  ) : (
                    <p className="max-w-36 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                      QR unavailable. Ask the front desk to reissue this ticket.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
